import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsApplicationsRoundedIcon from "@mui/icons-material/SettingsApplicationsRounded";
import { Box, Button, FormControlLabel, InputAdornment, Switch, TextField, Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppContext } from "../../AppContext";
import HelmetMeta from "../../components/HelmetMeta";
import { showReservationPrompt } from "../../components/ReservationPromptDialog";
import GroupList from "../../sections/restaurant/items/GroupList";
import SubGroupList from "../../sections/restaurant/items/SubGroupList";
import { PosContext } from "../../shared/PosContext";
import { getOrderDoctype } from "../../shared/draftOrderUtils";
import {
    useDeleteDoctype,
    useSaveUserSettings,
} from "../../shared/hooks/useFrappeDoctype";
import {
    useDeleteOrder,
    usePushItemToOrderData,
    useRemoveItemFromTableOrderData,
} from "../../shared/hooks/useTableOrderItemsData";
import {
    useAddNewOrder,
    useAddNewOrderDraft,
    usePushOrderItems,
    usePushOrderItemsDraft,
    useSendTableOrder,
    useTableOrdersLazy
} from "../../shared/hooks/useTableOrdersData";
import {
    cloneAddOn,
    defaultErrorToast,
    isJsonString,
    randomId,
} from "../../utils/floorUtils";
import AddItemWidget from "../Selling/Widgets/AddItemWidget";
import WidgetsDialogs from "../Widgets/WidgetsDialogs";
import OrderCart from "./components/OrderCart";
import PosItemList from "./components/PosItemList";
import { useStyles } from "./styles";

const fetchReservedDesignatedNumbers = async () => {
    try {
        const filters = [];
        const params = {
            doctype: "Draft Table Order",
            fields: JSON.stringify(["designated_number"]),
            filters: JSON.stringify(filters),
            order_by: "modified desc",
            limit_start: 0,
            limit_page_length: 500,
        };

        const { data } = await axios.get("method/frappe.client.get_list", {
            params,
        });

        const rows = data?.message || [];
        return rows
            .map((r) => r?.designated_number)
            .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
            .map((v) => parseInt(v, 10))
            .filter((n) => Number.isFinite(n) && n >= 1 && n <= 200);
    } catch (e) {
        // Fail open: if we can't fetch, still allow selecting any number.
        console.warn("Failed to fetch reserved designated numbers", e);
        return [];
    }
};
const PosPage = () => {
    const { t } = useTranslation();
    const {
        changeSelectedTable,
        selectedTable,
        isAppLoading,
        selectedOrder,
        setSelectedOrder,
        orderItems,
        getMenuItem,
        customerContext,
        addressContext,
        menuItems,
        setTakeawayOrder,
        addonsItems,
        groupedItems,
        refetchTablesData,
        refetchCustomers,
        refetchAddresses,
    } = useContext(PosContext);

    const {
        isTabsPos,
        userMenuSettings,
        setUserMenuSettings,
        elevatedPrivileges,
        restaurantSettings,
        menuRootGroupId,
        isCallCenterUser,
    } = useContext(AppContext);

    const rootGroupId = menuRootGroupId || "All Item Groups";

    const navigate = useNavigate();

    const { tableName, orderName } = useParams();
    const [searchParams] = useSearchParams();
    const isFromCancelDuplicate =
        searchParams.get("from_cancel_duplicate") === "1";

    const { mutate: deleteDoc } = useDeleteDoctype();

    const isTakeaway = useMemo(() => {
        return tableName && tableName === "takeaway" ? true : false;
    }, [tableName]);

    const [tmpOrderItems, setTmpOrderItems] = useState([]);

    const [selectedCustomer, setSelectedCustomer] = customerContext;
    const [selectedAddress, setSelectedAddress] = addressContext;

    // Optional preselect (used from OrderTabs -> "Create new order for customer")
    const preselectCustomer = searchParams.get("customer");
    const preselectAddress = searchParams.get("address");

    const classes = useStyles();

    const [toCloseTable, setToCloseTable] = useState(null);

    // const [selectedOrder, setSelectedOrder] = useState(null);

    const [category, setCategory] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subCategory, setSubCategory] = useState(null);
    const [currentSubGroups, setCurrentSubGroups] = useState([]);

    const [addItemOpen, setAddItemOpen] = useState(false);
    const [addedItem, setAddedItem] = useState(null);

    const [editOrderItemOpen, setEditOrderItemOpen] = useState(false);
    const [editedItem, setEditedItem] = useState(null);
    const [editedOrderItem, setEditedOrderItem] = useState(null);
    const [menuItems1, setMenuItems1] = useState(null);

    const pushItemToOrderMutation = usePushItemToOrderData(); //room, tableFormData
    const removeOrderItemMutation = useRemoveItemFromTableOrderData();
    const deleteOrderMutation = useDeleteOrder();

    const [items, setItems] = useState([]);
    const [savedItems, setSavedItems] = useState([]);
    const [hasDraftPendingChanges, setHasDraftPendingChanges] = useState(false);

    const { data: toCloseTableOrders, refetch: fetchCloseTableOrders } =
        useTableOrdersLazy(toCloseTable?.name || null);

    const [widgetDialogTable, setWidgetDialogTable] = useState(null);
    const [widgetsDialogsOpen, setWidgetsDialogsOpen] = useState(false);
    const [pendingAutoCloseOrderName, setPendingAutoCloseOrderName] =
        useState(null);

    const [isDragDropMode, setIsDragDropMode] = useState(false);
    const [isQuickAddMode, setIsQuickAddMode] = useState(false);
    const [searchText, setSearchText] = useState("");

    const saveUserSettings = useSaveUserSettings();
    const queryClient = useQueryClient();

    useEffect(() => {
        if (isTakeaway) {
            if (selectedOrder?.customer) {
                setSelectedCustomer(selectedOrder.customer);
            } else {
                // Don't override preselected (or already chosen) customer for new takeaway orders
                if (!selectedCustomer) {
                    setSelectedCustomer("takeaway-customer");
                }
            }
        }
    }, [isTakeaway, selectedOrder, selectedCustomer]);

    useEffect(() => {
        if (tableName === "takeaway" && !orderName) {
            if (preselectCustomer) {
                setSelectedCustomer(preselectCustomer);
                if (preselectAddress) {
                    setSelectedAddress(preselectAddress);
                }
            }
        }
    }, [
        tableName,
        orderName,
        preselectCustomer,
        preselectAddress,
        setSelectedCustomer,
        setSelectedAddress,
    ]);

    useEffect(() => {
        if (!isAppLoading) {
            if (tableName === "takeaway") {
                setTakeawayOrder(orderName);
            } else {
                changeSelectedTable(tableName, orderName);
            }
        }
    }, [isAppLoading, tableName, orderName]);

    useEffect(() => {
        if (tmpOrderItems.length > 0) {
            setItems(tmpOrderItems);
        } else {
            setItems([]);
        }
    }, [tmpOrderItems]);

    useEffect(() => {
        if (toCloseTable) fetchCloseTableOrders();
    }, [toCloseTable]);

    useEffect(() => {
        //-TODO NEW CHANGES
        if (toCloseTableOrders && toCloseTableOrders.length > 0) {
            setWidgetDialogTable(toCloseTable);
            setWidgetsDialogsOpen(true);
        } else {
            setWidgetDialogTable(null);
            setWidgetsDialogsOpen(false);
        }
    }, [toCloseTableOrders]);

    const updateOrderItems = (_orderItems) => {
        const _items = _orderItems || orderItems;
        const cartItems = [];
        _items
            .filter((i) => !i.parent_item || i.parent_item == "NULL")
            .forEach((i) => {
                const menuItem = getMenuItem(i.item_code);
                if (menuItem) {
                    const itemAddons = [];
                    const addonsArray = i.addons ? JSON.parse(i.addons) : [];

                    if (addonsArray && addonsArray.length > 0) {
                        addonsArray.map((ad) => {
                            if (typeof ad === "string") {
                                itemAddons.push(
                                    addonsItems.find(
                                        (entry) => entry.item_code === ad
                                    )
                                );
                            } else if (typeof ad === "object") {
                                itemAddons.push({
                                    ...addonsItems.find(
                                        (entry) =>
                                            entry.item_code === ad.item_code
                                    ),
                                    standard_rate: ad.standard_rate,
                                });
                            }
                        });
                    }
                    cartItems.push({
                        item: {
                            ...i,
                            cover: menuItem.cover,
                            standard_rate: menuItem.standard_rate,
                            // Needed for UI-only logic (e.g., hiding default bundle options)
                            menu_item_bundle: menuItem.menu_item_bundle,
                        },
                        addons: itemAddons,
                        qty: i.qty,
                        entry_name: menuItem.identifier,
                    });
                }
            });

        setSavedItems(cartItems);
    };

    useEffect(() => {
        if (orderItems && orderItems.length > 0 && addonsItems) {
            updateOrderItems();
        } else {
            setItems([]);
        }
    }, [orderItems, addonsItems]);

    useEffect(() => {
        if (isTakeaway) {
            if (!orderName) {
                setSavedItems([]);
            }
            setTmpOrderItems([]);
            setItems([]);
            setHasDraftPendingChanges(false);
        }
    }, [isTakeaway, orderName]);

    useEffect(() => {
        if (editedOrderItem) {
            let itemInOrder;
            if (menuItems1 && menuItems1.length > 0)
                itemInOrder = _.find(menuItems1, {
                    item_code: editedOrderItem.item_code,
                });

            if (itemInOrder) {
                setEditedItem(itemInOrder);
            }
        }
    }, [editedOrderItem]);

    // Load quick add mode from user settings
    useEffect(() => {
        if (userMenuSettings && userMenuSettings.quick_add_mode !== undefined) {
            setIsQuickAddMode(userMenuSettings.quick_add_mode);
        }
    }, [userMenuSettings]);

    // Save quick add mode when it changes
    const handleQuickAddModeChange = (newMode) => {
        setIsQuickAddMode(newMode);

        if (userMenuSettings) {
            const _userSettings = { ...userMenuSettings };
            _userSettings.quick_add_mode = newMode;

            // Update AppContext immediately for UI responsiveness
            setUserMenuSettings(_userSettings);

            saveUserSettings.mutate(
                {
                    doctype: "menu_settings",
                    user_settings: _userSettings,
                },
                {
                    onSuccess: () => {
                        // Invalidate the cache so fresh data is fetched on next page load
                        queryClient.invalidateQueries([
                            "GetUserSettings",
                            "menu_settings",
                        ]);
                        console.log("Quick add mode saved!");
                    },
                }
            );
        }
    };

    /**
     * Quick add item to cart without opening dialog
     * @param item
     */
    const quickAddItem = (item) => {
        // Check if item already exists in tmpOrderItems with no notes/addons
        const existingItemIndex = tmpOrderItems.findIndex(
            (orderItem) =>
                orderItem.item.item_code === item.item_code &&
                (!orderItem.notes || orderItem.notes === "") &&
                (!orderItem.addons || orderItem.addons.length === 0)
        );

        if (existingItemIndex !== -1) {
            // Item exists with no notes/addons, increment quantity
            setTmpOrderItems((prevState) => {
                const updatedItems = [...prevState];
                updatedItems[existingItemIndex] = {
                    ...updatedItems[existingItemIndex],
                    qty: updatedItems[existingItemIndex].qty + 1,
                };
                return updatedItems;
            });
        } else {
            // Item doesn't exist or has notes/addons, add new item
            const clonedItem = {
                item: {
                    ...item,
                    qty: 1,
                    notes: "",
                },
                qty: 1,
                addons: [],
                notes: "",
                entry_name: randomId(5),
            };

            addItemToOrder(clonedItem);
        }
    };

    /**
     * Pushes item to selected order.
     * @param item
     */
    const onItemClick = (item) => {
        const itemBudnle = item.menu_item_bundle
            ? JSON.parse(item.menu_item_bundle)
            : null;
        const skipQuickAdd =
            itemBudnle &&
            itemBudnle.categories &&
            itemBudnle.categories.length > 0;
        if (isQuickAddMode && !skipQuickAdd) {
            quickAddItem(item);
        } else {
            setAddedItem(item);
            setAddItemOpen(true);
        }
    };

    const handleAddItemClose = () => {
        setAddItemOpen(false);
        setEditOrderItemOpen(false);
        setEditedItem(null);
        setEditedOrderItem(null);
    };

    const handleInvoiceClosed = () => {
        if (isTakeaway) {
            refetchTablesData().then((res) => {
                const orders = res.data.message.takeaway?.orders;

                if (orders && orders[orderName]) {
                    const newItems = orders[orderName].entry_items.map((i) => {
                        const menuItem = getMenuItem(i.item_code);
                        if (menuItem) {
                            return {
                                item: {
                                    ...i,
                                    cover: menuItem.cover,
                                    standard_rate: menuItem.standard_rate,
                                },
                                addons: i.addons
                                    ? isJsonString(i.addons)
                                        ? JSON.parse(i.addons)
                                        : i.addons
                                    : [],
                                qty: i.qty,
                                entry_name: i.item_code,
                            };
                        }
                    });

                    setSavedItems(newItems);
                    handleCloseDialog();
                } else {
                    navigate("/tables");
                }
            });
        } else {
            navigate("/tables");
        }
    };

    const addItemToOrder = (item) => {
        setTmpOrderItems((prevState) => [...prevState, item]);
    };

    const mapAddonToOrderItemPayload = (addon) => {
        if (!addon) return null;
        if (typeof addon === "string") {
            return {
                item_code: addon,
            };
        }
        return {
            item_code: addon.item_code,
            item_name: addon.item_name,
            name: addon.name,
            identifier: addon.identifier,
            rate: addon.rate,
            standard_rate: addon.rate ?? addon.standard_rate,
            qty: addon.qty,
        };
    };

    const mapTmpCartItemToOrderItemPayload = (i) => ({
        identifier: i.item?.identifier || i.identifier,
        item: i.item?.item_code || i.item_code,
        qty: i.qty,
        standard_rate: i.item?.standard_rate ?? i.rate ?? 0,
        notes: i.notes || "",
        addons: (i.addons || []).map(mapAddonToOrderItemPayload).filter(Boolean),
    });

    const mapSavedCartItemToOrderItemPayload = (i) => ({
        identifier: i.item?.identifier || i.identifier,
        item: i.item?.item_code || i.item_code,
        qty: i.qty,
        standard_rate: i.item?.rate ?? i.item?.standard_rate ?? i.rate ?? 0,
        notes: i.notes || i.item?.notes || "",
        addons: (i.addons || []).map(mapAddonToOrderItemPayload).filter(Boolean),
    });

    const buildDraftOrderItemsPayload = ({ includeSavedItems = false } = {}) => {
        const newOrderItems = (tmpOrderItems || []).map(
            mapTmpCartItemToOrderItemPayload
        );
        if (!includeSavedItems) return newOrderItems;
        const persistedItems = (savedItems || []).map(
            mapSavedCartItemToOrderItemPayload
        );
        return [...persistedItems, ...newOrderItems];
    };

    const handleUpdateItem = (item, isNew) => {
        const itemToUpdate = { ...item };

        let updatedAddons = [];
        itemToUpdate.addons.forEach((addon) => {
            // addon can be a string (item_code) or an object { item_code, standard_rate }
            const addonCode =
                typeof addon === "string" ? addon : addon.item_code;
            const overrideRate =
                typeof addon === "object" && addon?.standard_rate != null
                    ? Number(addon.standard_rate)
                    : null;

            let addonItem = _.find(
                addonsItems,
                (it) => it.item_code === addonCode
            );
            if (!addonItem) {
                addonItem = {
                    item_code: addonCode,
                    standard_rate: overrideRate || 0,
                };
            }

            updatedAddons.push(
                cloneAddOn(
                    addonItem,
                    itemToUpdate.qty,
                    itemToUpdate.identifier,
                    overrideRate
                )
            );
        });
        itemToUpdate.addons = updatedAddons.length > 0 ? updatedAddons : [];

        let didUpdateTmpOrder = false;
        const currentOrders = [...tmpOrderItems];
        const updatedOrders = currentOrders.map((i) => {
            if (i.entry_name === item.entry_name) {
                didUpdateTmpOrder = true;
                return itemToUpdate;
            }
            return i;
        });

        if (didUpdateTmpOrder) {
            if (selectedOrder?.status === "Draft") {
                setHasDraftPendingChanges(true);
            }
            setTmpOrderItems(updatedOrders);
            return;
        }

        // Editing a persisted draft row updates savedItems directly.
        const targetIdentifier = itemToUpdate?.item?.identifier;
        setSavedItems((prevSavedItems) =>
            (prevSavedItems || []).map((savedItem) => {
                const sameIdentifier =
                    !!targetIdentifier &&
                    savedItem?.item?.identifier === targetIdentifier;
                const sameEntryName =
                    !!savedItem?.entry_name &&
                    !!itemToUpdate?.entry_name &&
                    savedItem.entry_name === itemToUpdate.entry_name;
                return sameIdentifier || sameEntryName
                    ? itemToUpdate
                    : savedItem;
            })
        );
        if (selectedOrder?.status === "Draft") {
            setHasDraftPendingChanges(true);
        }
    };
    const handleAddItem = (item, qty, addons, notes) => {
        // onItemClick(item, qty);

        let clonedItem = {};
        clonedItem.item = {
            ...item,
            ["notes"]: notes,
            ["qty"]: qty,
        };

        if (qty) clonedItem.qty = qty;

        if (notes && notes.length > 0) {
            clonedItem.notes = notes;
        }

        let addonsToSave = [];
        if (addons) {
            addons.forEach((aItem) => {
                // aItem can be a string (item_code) or object { item_code, standard_rate }
                const addonCode =
                    typeof aItem === "string" ? aItem : aItem.item_code;
                const overrideRate =
                    typeof aItem === "object" && aItem?.standard_rate != null
                        ? Number(aItem.standard_rate)
                        : null;
                let addonItem = _.find(
                    addonsItems,
                    (it) => it.item_code === addonCode
                );
                if (!addonItem) {
                    addonItem = {
                        item_code: addonCode,
                        standard_rate: overrideRate || 0,
                    };
                }
                addonsToSave.push(
                    cloneAddOn(
                        addonItem,
                        clonedItem.qty,
                        clonedItem.identifier,
                        overrideRate
                    )
                );
            });
            clonedItem.addons = addonsToSave.length > 0 ? addonsToSave : [];

            clonedItem.entry_name = randomId(5);

            if (selectedOrder && selectedOrder.name) {
                addItemToOrder({
                    ...clonedItem,
                    ["notes"]: notes,
                    ["qty"]: qty,
                });
            } else {
                addItemToOrder({
                    ...clonedItem,
                    ["notes"]: notes,
                    ["qty"]: qty,
                });
                // addItemToOrder(clonedItem);
            }

            setAddItemOpen(false);
            setEditOrderItemOpen(false);
        }
    };

    const onGroupClicked = (group) => {
        if (group && !group.name) {
            setCategory(null);
            setSelectedCategory(null);
            setSubCategory(null);
            setCurrentSubGroups([]);
            return;
        }

        setCategory(group);

        // const { id, name, cover } = group;
        setSelectedCategory(group);
        setSubCategory(null);
        if (group?.id === rootGroupId) {
            setCurrentSubGroups([]);
        }
    };

    const onSubGroupClicked = (subGroup) => {
        if (subGroup && !subGroup.name) {
            setSubCategory(null);
            return;
        }
        // If the clicked subgroup is itself a group, drill down (treat it as the new category)
        if (subGroup && subGroup.is_group) {
            onGroupClicked(subGroup);
            return;
        }
        setSubCategory(subGroup);
    };

    const handleSelectOrder = (order) => {
        navigate(`/pos/${tableName}/${order.name}`);
    };

    const handleCloseTable = (order, table) => {
        if (isCallCenterUser) {
            // Call center flow: no payment screen.
            return;
        }
        setWidgetDialogTable(table);
        setWidgetsDialogsOpen(true);
    };

    // Tabs POS enhancement: after Send to kitchen success, auto-open close/payment flow
    useEffect(() => {
        if (!isTabsPos) return;
        if (!pendingAutoCloseOrderName) return;
        if (!selectedOrder?.name) return;
        if (selectedOrder.name !== pendingAutoCloseOrderName) return;

        setPendingAutoCloseOrderName(null);
        if (!isCallCenterUser) {
        if (!isCallCenterUser) {
            handleCloseTable(selectedOrder, selectedTable);
        }
        }
    }, [isTabsPos, pendingAutoCloseOrderName, selectedOrder?.name, isCallCenterUser]);

    const mutateNewOrder = useAddNewOrder();
    const mutatePushOrderItems = usePushOrderItems();
    const mutateNewOrderDraft = useAddNewOrderDraft();
    const mutatePushOrderItemsDraft = usePushOrderItemsDraft();
    const sendDraftOrderMutation = useSendTableOrder();

    const isSendingToKitchen =
        mutateNewOrder.isLoading || mutatePushOrderItems.isLoading;
    const isSavingDraft =
        mutateNewOrderDraft.isLoading || mutatePushOrderItemsDraft.isLoading;
    const isSendingDraftOrder = sendDraftOrderMutation.isLoading;
    const shouldSkipPrintAfterDuplicate =
        isFromCancelDuplicate &&
        Number(restaurantSettings?.docs?.[0]?.print_after_duplicate) !== 1;

    const handleSaveToTable = async (table) => {
        const newOrderItems = [];
        tmpOrderItems.map((i) => {
            newOrderItems.push({
                addons: i.addons.map((addon) => ({
                    item_code: addon.item_code,
                    item_name: addon.item_name,
                    name: addon.name,
                    identifier: addon.identifier,
                    rate: addon.rate,
                    standard_rate: addon.rate,
                    qty: addon.qty,
                })),
                qty: i.qty,
                item: i.item.item_code,
                notes: i.notes,
                standard_rate: i.item.standard_rate,
            });
        });

        const rateChanged = [];

        // selectedOrder;

        newOrderItems.map((i) => {
            const menuItem = menuItems.find(
                (item) => item.item_code === i.item
            );
            if (menuItem) {
                if (menuItem.standard_rate !== i.standard_rate) {
                    rateChanged.push({
                        ...i,
                        originalRate: menuItem.standard_rate,
                    });
                }
            }
        });

        if (selectedOrder) {
            setTmpOrderItems([])
            mutatePushOrderItems.mutate(
                {
                    orderItems: newOrderItems,
                    order: selectedOrder,
                    elevatedPrivileges:
                        elevatedPrivileges && rateChanged.length > 0,
                    elevatedText: rateChanged.length > 0 ? "Rate Changed" : "",
                },
                {
                    onSuccess: () => {
                        refetchTablesData().then((res) => {
                            if (isTabsPos) {
                                const payload = res?.data?.message || {};
                                const allTables = [
                                    payload.takeaway,
                                    ...Object.values(payload.rooms || {}).flatMap(
                                        (room) => Object.values(room?.tables || {})
                                    ),
                                ].filter(Boolean);
                                const refreshedOrder = allTables
                                    .flatMap((table) => Object.values(table?.orders || {}))
                                    .find((order) => order?.name === orderName);
                                updateOrderItems(
                                    refreshedOrder?.entry_items
                                );

                                setTmpOrderItems([]);
                                setItems([]);
                            }
                        });

                        if (isTabsPos) {
                            // Auto open close/payment flow after kitchen send (OrderTabs flow)
                            if (!isCallCenterUser) {
                                handleCloseTable(selectedOrder, selectedTable);
                            }
                        } else {
                            navigate("/tables");
                        }
                    },
                    onError: (error) => {
                        defaultErrorToast(error);
                    },
                }
            );
        } else {
            let promptData = null;
            if (restaurantSettings?.docs[0]?.mandatory_notes == 1 || isCallCenterUser) {
                const reservedDesignatedNumbers = (isTabsPos || isCallCenterUser)
                    ? await fetchReservedDesignatedNumbers()
                    : [];

                promptData = await showReservationPrompt({
                    title: t("pos.orderNotesTitle"),
                    message: t("pos.orderNotesPrompt"),
                    confirmText: t("pos.createOrder"),
                    cancelText: t("common.cancel"),
                    isTabsPos: isTabsPos || isCallCenterUser,
                    reservedDesignatedNumbers,
                });

                if (!promptData) return;
            }

            const notesText = promptData?.notes || "";

            mutateNewOrder.mutate(
                {
                    orderItems: newOrderItems,
                    table: selectedTable.name,
                    customer: isTabsPos ? promptData?.customer : selectedCustomer,
                    address: isTabsPos ? undefined : selectedAddress,
                    address_line1: isTabsPos ? promptData?.address_line1 : undefined,
                    address_line2: isTabsPos ? promptData?.address_line2 : undefined,
                    mobile_no: isTabsPos ? promptData?.mobile_no : undefined,
                    delivery_provider: isTabsPos ? promptData?.delivery_provider : undefined,
                    dinners: isTabsPos ? undefined : promptData?.adults,
                    dinners_children: isTabsPos ? undefined : promptData?.kids,
                    dinners_infants: isTabsPos ? undefined : promptData?.babies,
                    estimated_preparation_time: isTabsPos
                        ? promptData?.estimated_preparation_time
                        : undefined,
                    designated_number: isTabsPos
                        ? promptData?.designated_number || undefined
                        : undefined,
                    notes: notesText,
                    call_center_order: isCallCenterUser ? 1 : undefined,
                    elevatedPrivileges:
                        elevatedPrivileges && rateChanged.length > 0,
                    elevatedText:
                        rateChanged.length > 0 ? t("pos.rateChanged") : "",
                },
                {
                    onSuccess: (res) => {
                        // Ensure any newly created customer/address shows up across the app (OrderTabs cards, dialogs, etc.)
                        refetchCustomers?.();
                        refetchAddresses?.();
                        if (isTakeaway) {
                            refetchTablesData().then(() => {
                                setTmpOrderItems([]);
                                if (isTabsPos) {
                                    // After navigation, selectedOrder will be loaded; then we auto-open close/payment
                                    setPendingAutoCloseOrderName(res.message);
                                }
                                navigate("/pos/takeaway/" + res.message);
                            });
                        } else {
                            refetchTablesData();
                            navigate("/tables");
                        }
                    },
                    onError: (error) => {
                        defaultErrorToast(error);
                    },
                }
            );
        }
        return;
    };

    const handleSaveDraftToTable = async (table, options = {}) => {
        const hasProvidedPromptData = Object.prototype.hasOwnProperty.call(
            options,
            "promptData"
        );
        const providedPromptData = hasProvidedPromptData
            ? options.promptData
            : null;
        const newOrderItems = [];
        tmpOrderItems.map((i) => {
            newOrderItems.push({
                addons: i.addons.map((addon) => ({
                    item_code: addon.item_code,
                    item_name: addon.item_name,
                    name: addon.name,
                    identifier: addon.identifier,
                    rate: addon.rate,
                    standard_rate: addon.rate,
                    qty: addon.qty,
                })),
                qty: i.qty,
                item: i.item.item_code,
                notes: i.notes,
                standard_rate: i.item.standard_rate,
            });
        });

        const rateChanged = [];
        newOrderItems.map((i) => {
            const menuItem = menuItems.find(
                (item) => item.item_code === i.item
            );
            if (menuItem) {
                if (menuItem.standard_rate !== i.standard_rate) {
                    rateChanged.push({
                        ...i,
                        originalRate: menuItem.standard_rate,
                    });
                }
            }
        });

        // Always show dialog if mandatory notes are enabled, regardless of whether it's a new or existing order
        let promptData = providedPromptData;
        if (
            !hasProvidedPromptData &&
            (restaurantSettings?.docs[0]?.mandatory_notes == 1 || isCallCenterUser)
        ) {
            const reservedDesignatedNumbers = (isTabsPos || isCallCenterUser)
                ? await fetchReservedDesignatedNumbers()
                : [];

            // Pre-fill with existing order data if updating an existing draft
            const initialData = selectedOrder ? {
                notes: selectedOrder.notes || "",
                adults: selectedOrder.dinners || "",
                kids: selectedOrder.dinners_children || "",
                babies: selectedOrder.dinners_infants || "",
                estimated_preparation_time: selectedOrder.estimated_preparation_time || 15,
                designated_number: selectedOrder.designated_number || "",
                customer: selectedOrder.customer || "",
                mobile_no: selectedOrder.mobile_no || "",
                address_line1: selectedOrder.address_line1 || "",
                address_line2: selectedOrder.address_line2 || "",
                delivery_provider: selectedOrder.delivery_provider || "",
                use_delivery_provider: !!selectedOrder.delivery_provider,
            } : undefined;

            promptData = await showReservationPrompt({
                title: t("pos.orderNotesTitle"),
                message: t("pos.orderNotesPrompt"),
                confirmText: selectedOrder ? t("common.save") : t("pos.createOrder"),
                cancelText: t("common.cancel"),
                isTabsPos: isTabsPos || isCallCenterUser,
                reservedDesignatedNumbers,
                initialData,
            });

            if (!promptData) return;
        }

        const notesText = promptData?.notes || "";

        if (selectedOrder) {
            setTmpOrderItems([]);
            
            // If dialog was shown and user provided data, update order metadata first
            const shouldUpdateMetadata = promptData && (restaurantSettings?.docs[0]?.mandatory_notes == 1 || isCallCenterUser);
            
            if (shouldUpdateMetadata) {
                // Build update payload based on mode
                const metadataPayload = {
                    notes: notesText,
                };

                if (isTabsPos) {
                    metadataPayload.estimated_preparation_time = promptData.estimated_preparation_time;
                    metadataPayload.designated_number = promptData.designated_number || "";
                    metadataPayload.customer = promptData.customer || "";
                    metadataPayload.mobile_no = promptData.mobile_no || "";
                    metadataPayload.address_line1 = promptData.address_line1 || "";
                    metadataPayload.address_line2 = promptData.address_line2 || "";
                    metadataPayload.delivery_provider = promptData.delivery_provider || "";
                } else {
                    if (promptData.adults !== undefined) metadataPayload.dinners = promptData.adults;
                    if (promptData.kids !== undefined) metadataPayload.dinners_children = promptData.kids;
                    if (promptData.babies !== undefined) metadataPayload.dinners_infants = promptData.babies;
                }

                // Update order metadata using axios directly
                await axios.post("method/frappe.client.set_value", {
                    doctype: getOrderDoctype(selectedOrder),
                    name: selectedOrder.name,
                    fieldname: metadataPayload,
                });
            }
            
            mutatePushOrderItemsDraft.mutate(
                {
                    orderItems: buildDraftOrderItemsPayload({
                        includeSavedItems: true,
                    }),
                    order: selectedOrder,
                    replaceExisting: true,
                    elevatedPrivileges:
                        elevatedPrivileges && rateChanged.length > 0,
                    elevatedText:
                        rateChanged.length > 0 ? t("pos.rateChanged") : "",
                },
                {
                    onSuccess: () => {
                        if (shouldUpdateMetadata) {
                            refetchCustomers?.();
                            refetchAddresses?.();
                        }
                        enqueueSnackbar(t("pos.savedAsDraft"), {
                            variant: "success",
                        });

                        // Draft orders are NOT in floor data, so re-fetch the
                        // draft doc directly to get the updated entry_items.
                        const doctype = getOrderDoctype(selectedOrder);
                        axios
                            .get("method/frappe.client.get", {
                                params: {
                                    doctype,
                                    name: selectedOrder.name,
                                },
                            })
                            .then(({ data }) => {
                                if (data?.message) {
                                    setSelectedOrder(data.message);
                                    updateOrderItems(
                                        data.message.entry_items
                                    );
                                }
                            })
                            .finally(() => {
                                setTmpOrderItems([]);
                                setItems([]);
                                setHasDraftPendingChanges(false);
                            });
                    },
                    onError: (error) => {
                        defaultErrorToast(error);
                    },
                }
            );
        } else {
            mutateNewOrderDraft.mutate(
                {
                    orderItems: newOrderItems,
                    table: selectedTable.name,
                    customer: isTabsPos ? promptData?.customer : selectedCustomer,
                    address: isTabsPos ? undefined : selectedAddress,
                    address_line1: isTabsPos ? promptData?.address_line1 : undefined,
                    address_line2: isTabsPos ? promptData?.address_line2 : undefined,
                    mobile_no: isTabsPos ? promptData?.mobile_no : undefined,
                    delivery_provider: isTabsPos ? promptData?.delivery_provider : undefined,
                    dinners: isTabsPos ? undefined : promptData?.adults,
                    dinners_children: isTabsPos ? undefined : promptData?.kids,
                    dinners_infants: isTabsPos ? undefined : promptData?.babies,
                    estimated_preparation_time: isTabsPos
                        ? promptData?.estimated_preparation_time
                        : undefined,
                    designated_number: isTabsPos
                        ? promptData?.designated_number || undefined
                        : undefined,
                    notes: notesText,
                    call_center_order: isCallCenterUser ? 1 : undefined,
                    elevatedPrivileges:
                        elevatedPrivileges && rateChanged.length > 0,
                    elevatedText:
                        rateChanged.length > 0 ? t("pos.rateChanged") : "",
                },
                {
                    onSuccess: (res) => {
                        // Ensure any newly created customer/address shows up across the app (OrderTabs cards, dialogs, etc.)
                        refetchCustomers?.();
                        refetchAddresses?.();
                        enqueueSnackbar(t("pos.savedAsDraft"), {
                            variant: "success",
                        });
                        // In tabs POS, drafts won't show in floor data (filtered), so return to OrderTabs
                        if (isTabsPos) {
                            setTmpOrderItems([]);
                            setItems([]);
                            navigate("/tables");
                            return;
                        }

                        if (isTakeaway) {
                            refetchTablesData().then(() => {
                                setTmpOrderItems([]);
                                navigate("/pos/takeaway/" + res.message);
                            });
                        } else {
                            refetchTablesData();
                            navigate("/tables");
                        }
                    },
                    onError: (error) => {
                        defaultErrorToast(error);
                    },
                }
            );
        }
        return;
    };

    const handleSendDraftOrder = async () => {
        if (!selectedOrder?.name) return;

        // Show dialog if mandatory notes are enabled
        let promptData = null;
        if (restaurantSettings?.docs[0]?.mandatory_notes == 1 || isCallCenterUser) {
            const reservedDesignatedNumbers = (isTabsPos || isCallCenterUser)
                ? await fetchReservedDesignatedNumbers()
                : [];

            promptData = await showReservationPrompt({
                title: t("pos.orderNotesTitle"),
                message: t("pos.orderNotesPrompt"),
                confirmText: t("pos.sendToKitchen"),
                showSaveDraftButton: true,
                saveDraftText: t("common.save"),
                cancelText: t("common.cancel"),
                isTabsPos: isTabsPos || isCallCenterUser,
                reservedDesignatedNumbers,
                // Pre-fill with existing order data
                initialData: {
                    notes: selectedOrder.notes || "",
                    adults: selectedOrder.dinners || "",
                    kids: selectedOrder.dinners_children || "",
                    babies: selectedOrder.dinners_infants || "",
                    estimated_preparation_time: selectedOrder.estimated_preparation_time || 15,
                    designated_number: selectedOrder.designated_number || "",
                    customer: selectedOrder.customer || "",
                    mobile_no: selectedOrder.mobile_no || "",
                    address_line1: selectedOrder.address_line1 || "",
                    address_line2: selectedOrder.address_line2 || "",
                    delivery_provider: selectedOrder.delivery_provider || "",
                    use_delivery_provider: !!selectedOrder.delivery_provider,
                },
            });

            if (!promptData) return;
            if (promptData?.__action === "save_draft") {
                await handleSaveDraftToTable(selectedTable, { promptData });
                return;
            }
        }

        const orderItems = buildDraftOrderItemsPayload({
            includeSavedItems: true,
        });

        // Resolve metadata -- prefer promptData values, fallback to selectedOrder
        const customer = promptData?.customer || selectedOrder.customer || "";
        const notes = promptData?.notes ?? selectedOrder.notes ?? "";
        const dinners = promptData?.adults ?? selectedOrder.dinners ?? 0;
        const dinners_children = promptData?.kids ?? selectedOrder.dinners_children ?? 0;
        const dinners_infants = promptData?.babies ?? selectedOrder.dinners_infants ?? 0;
        const estimated_preparation_time =
            promptData?.estimated_preparation_time ??
            selectedOrder.estimated_preparation_time ??
            undefined;
        const designated_number =
            promptData?.designated_number ?? selectedOrder.designated_number ?? undefined;
        const delivery_provider =
            promptData?.delivery_provider ?? selectedOrder.delivery_provider ?? undefined;
        const mobile_no = promptData?.mobile_no ?? selectedOrder.mobile_no ?? undefined;
        const address_line1 =
            promptData?.address_line1 ?? selectedOrder.address_line1 ?? undefined;
        const address_line2 =
            promptData?.address_line2 ?? selectedOrder.address_line2 ?? undefined;

        sendDraftOrderMutation.mutate(
            {
                table: selectedOrder.table,
                draftName: selectedOrder.name,
                orderItems,
                customer,
                address: selectedOrder.address || undefined,
                mobile_no,
                address_line1,
                address_line2,
                dinners,
                dinners_children,
                dinners_infants,
                notes,
                estimated_preparation_time,
                designated_number,
                delivery_provider,
                call_center_order: isCallCenterUser ? 1 : undefined,
                no_print: shouldSkipPrintAfterDuplicate ? true : undefined,
            },
            {
                onSuccess: (res) => {
                    enqueueSnackbar(t("pos.draftSaved"), { variant: "success" });
                    // The backend created a new Table Order and marked the draft as Attending.
                    // Navigate to the new order if the backend returned its name.
                    const newOrderName = res?.message;
                    if (newOrderName && typeof newOrderName === "string") {
                        refetchTablesData?.().then(() => {
                            setTmpOrderItems([]);
                            setHasDraftPendingChanges(false);
                            if (isTabsPos) {
                                // Auto-open payment dialog after navigating to the new order
                                setPendingAutoCloseOrderName(newOrderName);
                            }
                            navigate(`/pos/takeaway/${newOrderName}`);
                        });
                    } else {
                        setHasDraftPendingChanges(false);
                        refetchTablesData?.();
                        navigate("/tables");
                    }
                },
                onError: (error) => {
                    defaultErrorToast(error);
                },
            }
        );
    };

    const handleCloseDialog = () => {
        setWidgetsDialogsOpen(false);
        setToCloseTable(null);
    };

    const handleItemDelete = (orderItem, props) => {
        setTmpOrderItems((current) =>
            current.filter((item) => item.entry_name !== orderItem.entry_name)
        );
    };

    const [itemDeleting, setItemDeleting] = useState(false);
    const [deletingItemIdentifier, setDeletingItemIdentifier] = useState(null);

    const handleDeleteOrder = (payload, callbacks = {}) => {
        if (!payload?.orderId || deleteOrderMutation.isLoading) return;

        const mutationPayload = {
            orderId: payload.orderId,
            notes: payload.notes || "",
            waste: !!payload.waste,
            doctype: getOrderDoctype(payload.orderId),
        };

        if (payload.masterPassword) {
            mutationPayload.masterPassword = payload.masterPassword;
        }

        deleteOrderMutation.mutate(mutationPayload, {
            onSuccess: () => {
                setSavedItems([]);
                setTmpOrderItems([]);
                refetchTablesData();
                enqueueSnackbar(t("pos.orderDeletedSuccessfully"), {
                    variant: "success",
                });
                navigate("/tables");
                callbacks?.onSuccess?.();
            },
            onError: (error) => {
                // defaultErrorToast(error);
                callbacks?.onError?.(error);
            },
            onSettled: (data, error) => {
                callbacks?.onSettled?.(data, error);
            },
        });
    };

    const handleSubmittedItemDelete = (orderItem, password, qty, notes, waste) => {
        setItemDeleting(true);
        setDeletingItemIdentifier(
            orderItem?.item?.identifier || orderItem?.entry_name || null
        );

        // If this deletion would leave the cart empty, delete the whole order
        // instead of deleting the last entry item.
        const orderItemIdentifier = orderItem?.item?.identifier;
        const qtyToDelete =
            qty ??
            orderItem?.qty ??
            orderItem?.item?.qty ??
            orderItem?.item?.quantity ??
            0;

        const remainingSavedItems = [];
        if (savedItems && savedItems.length > 0) {
            savedItems.forEach((i) => {
                const sameIdentifier =
                    i?.item?.identifier && orderItemIdentifier
                        ? i.item.identifier === orderItemIdentifier
                        : false;

                if (!sameIdentifier) {
                    remainingSavedItems.push(i);
                    return;
                }

                const currentQty =
                    i?.qty ?? i?.item?.qty ?? i?.item?.quantity ?? 0;
                const nextQty = Number(currentQty) - Number(qtyToDelete);

                if (nextQty > 0) {
                    remainingSavedItems.push({
                        ...i,
                        qty: nextQty,
                    });
                }
            });
        }

        const willCartBeEmpty =
            (!items || items.length === 0) && remainingSavedItems.length === 0;

        if (willCartBeEmpty && selectedOrder?.name) {
            const payload = {
                orderId: selectedOrder.name,
                notes: notes || "",
                waste: !!waste,
            };

            if (elevatedPrivileges) {
                payload.masterPassword = password;
            }

            // Keep per-item deleting UI visible until navigation
            handleDeleteOrder(payload, {
                onSettled: () => {
                    setItemDeleting(false);
                },
            });
            return;
        }

        const orderDoctype = getOrderDoctype(selectedOrder);

        const removeOrderItemPayload = {
            orderId: selectedOrder.name,
            item: orderItem.item,
            doctype: orderDoctype,
        };

        if (elevatedPrivileges) {
            removeOrderItemPayload.masterPassword = password;
        }

        if (qty) {
            removeOrderItemPayload.qty = qty;
        }

        if (waste) {
            removeOrderItemPayload.waste = waste;
        }

        removeOrderItemMutation.mutate(removeOrderItemPayload, {
            onSuccess: () => {
                refetchTablesData();

                const newSavedItems = [];
                savedItems.forEach((i) => {
                    if (i.item.identifier === orderItem.item.identifier) {
                        if (i.item.qty > qty) {
                            newSavedItems.push({
                                ...i,
                                qty: i.item.qty - qty,
                            });
                        }
                    } else {
                        newSavedItems.push(i);
                    }
                });

                setSavedItems(newSavedItems);

                // Only auto-delete the order if cart is truly empty
                if (newSavedItems.length === 0 && (!items || items.length === 0)) {
                    deleteDoc({
                        doctype: orderDoctype,
                        name: selectedOrder.name,
                    });
                    refetchTablesData();
                    navigate("/tables");
                    // setSelectedOrder(null);
                    // setOrders([]);
                }
            },
            onError: (error) => {
                // defaultErrorToast(error);
            },
            onSettled: () => {
                setItemDeleting(false);
                setDeletingItemIdentifier(null);
            },
        });
    };

    // Define the event handler function
    const handleItemEdit = (orderItem) => {
        setEditedOrderItem(orderItem);
        setAddedItem(orderItem.item);
        setEditOrderItemOpen(true);
    };

    const onItemsLoaded = (items) => {
        setMenuItems1(items);
    };

    useEffect(() => {
        setHasDraftPendingChanges(false);
    }, [selectedOrder?.name]);

    return (
        <React.Fragment>
            <div className={classes.NotificationSnackbarIcon}></div>
            <div className={classes.PageWrap}>
                <HelmetMeta title={t("pos.helmetTitle")} />
                {/* isTabsPos order switching is handled via "Change Order" in OrderCart */}
                <div
                    className={classes.NewOrderPage}
                >
                    <div className={classes.Categories}>
                        <div className={"CategoriesList"}>
                            <GroupList
                                onClick={onGroupClicked}
                                isDragDropMode={isDragDropMode}
                            ></GroupList>
                        </div>
                    </div>
                    <div
                        className={classes.CategoryView}
                    >
                        <Box className={"CategoryView__Header"}>
                            <Box 
                                sx={{
                                    display: "block",
                                }}
                            >
                                {/* Controls Row */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1.5,
                                        gap: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <TextField
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        size="small"
                                        placeholder={t("pos.searchItemsPlaceholder")}
                                        sx={{ 
                                            minWidth: { xs: 140, sm: 180 },
                                            maxWidth: { xs: 200, sm: 250 },
                                            flexGrow: 1,
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={isQuickAddMode}
                                                    onChange={(e) =>
                                                        handleQuickAddModeChange(
                                                            e.target.checked
                                                        )
                                                    }
                                                    color="primary"
                                                />
                                            }
                                            label={t("pos.quickAdd")}
                                            sx={{
                                                margin: 0,
                                                "& .MuiFormControlLabel-label": {
                                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                                    fontWeight: 500,
                                                },
                                            }}
                                        />
                                        <Tooltip
                                            title={
                                                isDragDropMode
                                                    ? t("pos.exitSettingsMode")
                                                    : t("pos.enterSettingsMode")
                                            }
                                        >
                                            <Button
                                                onClick={() =>
                                                    setIsDragDropMode(!isDragDropMode)
                                                }
                                                sx={{
                                                    fontSize: "1.5rem",
                                                    minWidth: "auto",
                                                    padding: { xs: "6px", sm: "8px" },
                                                }}
                                                startIcon={
                                                    isDragDropMode ? (
                                                        <SettingsApplicationsRoundedIcon />
                                                    ) : (
                                                        <SettingsIcon />
                                                    )
                                                }
                                            >
                                                {isDragDropMode ? "" : ""}
                                            </Button>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                
                                {/* Subcategories Row */}
                                <Box 
                                    className={"Subcatgories"}
                                    sx={{
                                        height: "50px",
                                        width: "100%",
                                    }}
                                >
                                    {selectedCategory &&
                                    selectedCategory.id !== rootGroupId ? (
                                        <SubGroupList
                                            parentItem={selectedCategory}
                                            onClick={onSubGroupClicked}
                                            isDragDropMode={isDragDropMode}
                                            onSubGroupsLoaded={
                                                setCurrentSubGroups
                                            }
                                        ></SubGroupList>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>
                        <div
                            className={classes.CategoryViewWrapper}
                            style={{
                                overflowY: "auto",
                                overflowX: "hidden",
                            }}
                        >
                            <PosItemList
                                groupedItems={groupedItems}
                                subGroups={currentSubGroups}
                                menuItems={menuItems}
                                onClick={onItemClick}
                                category={category}
                                subCategory={subCategory}
                                onItemsLoaded={onItemsLoaded}
                                isDragDropMode={isDragDropMode}
                                searchText={searchText}
                            ></PosItemList>
                        </div>
                    </div>
                    <div className={classes.Order}>
                        {selectedTable && (
                            <OrderCart
                                selectedOrder={selectedOrder}
                                items={items}
                                savedItems={savedItems}
                                table={selectedTable}
                                isDeletingOrder={deleteOrderMutation.isLoading}
                                onDeleteOrder={handleDeleteOrder}
                                deletingItemIdentifier={deletingItemIdentifier}
                                handleSaveToTable={handleSaveToTable}
                                handleSaveToTableDraft={
                                    isTabsPos ? handleSaveDraftToTable : undefined
                                }
                                handleSendDraftOrder={handleSendDraftOrder}
                                isSendingToKitchen={isSendingToKitchen}
                                isSavingDraft={isSavingDraft}
                                isSendingDraftOrder={isSendingDraftOrder}
                                handleSelectOrder={handleSelectOrder}
                                handleCloseTable={handleCloseTable}
                                // isOrderItemsLoading={isOrderItemsLoading}
                                isTakeaway={isTakeaway}
                                onItemEdit={handleItemEdit}
                                onItemDelete={handleItemDelete}
                                onSubmittedItemDelete={
                                    handleSubmittedItemDelete
                                }
                                itemDeleting={itemDeleting}
                                hasDraftPendingChanges={hasDraftPendingChanges}
                            />
                        )}
                    </div>
                </div>
            </div>

            <WidgetsDialogs
                open={widgetsDialogsOpen}
                onClose={handleCloseDialog}
                order={selectedOrder}
                table={widgetDialogTable}
                isTakeaway={isTakeaway}
                onInvoiceClose={handleInvoiceClosed}
            ></WidgetsDialogs>

            <AddItemWidget
                item={addedItem}
                open={addItemOpen || editOrderItemOpen}
                onClose={handleAddItemClose}
                onAddItem={handleAddItem}
                onUpdateItem={handleUpdateItem}
                editedOrderItem={editedOrderItem}
                pushItemWorker={pushItemToOrderMutation}
                readOnly={false}
            />
        </React.Fragment>
    );
};

export default PosPage;
